import { apiGet, apiPut } from '@/api/client';
import type { AccentColor, Company, CompanySettings } from '@/api/types';

/**
 * `GET /companies/{companyId}` (§ Instellingen › Profiel, PLAN-FASE2.md
 * WP-08): organisatienaam, branche and the derived `employee_count`.
 */
export function getCompany(companyId: string): Promise<Company> {
  return apiGet('/companies/{companyId}', { params: { companyId } });
}

/**
 * `PUT /companies/{companyId}` writes the whole editable record, same
 * whole-record contract as `ProfileUpdate` in `features/profile/api.ts` —
 * `industry` is sent alongside `name` even when only one of the two changed.
 */
export interface CompanyProfileUpdate {
  name: string;
  industry: string | null;
}

export function updateCompany(
  companyId: string,
  body: CompanyProfileUpdate,
): Promise<{ company_id?: string } | undefined> {
  return apiPut('/companies/{companyId}', { params: { companyId }, body });
}

/**
 * `GET /companies/{companyId}/settings` (§ Instellingen › Huisstijl,
 * Privacy): min_group_size, logo/accent and the four privacy toggles.
 * Readable by a company admin; only an admin may write it (see
 * `putCompanySettings`) — `permissions.canUpdateCompanySettings` in
 * `src/features/me/permissions.ts` mirrors the API's own gate.
 */
export function getCompanySettings(companyId: string): Promise<CompanySettings> {
  return apiGet('/companies/{companyId}/settings', {
    params: { companyId },
  }) as Promise<CompanySettings>;
}

export interface CompanySettingsUpdate {
  min_group_size: number | null;
  logo_url?: string | null;
  accent_color?: AccentColor | null;
  privacy_aggregated_data?: boolean;
  privacy_explicit_consent?: boolean;
  privacy_data_export?: boolean;
  privacy_audit_log?: boolean;
}

export function updateCompanySettings(
  companyId: string,
  body: CompanySettingsUpdate,
): Promise<{ company_id?: string } | undefined> {
  return apiPut('/companies/{companyId}/settings', { params: { companyId }, body });
}

/**
 * `GET /companies/{companyId}/modules` (PLAN-FASE3.md WP-00): the 8
 * fixed modules and their enabled/disabled state. Readable by a company
 * admin; only an admin may write it (see `updateCompanyModules`).
 */
export function getCompanyModules(companyId: string): Promise<Record<string, boolean>> {
  return apiGet('/companies/{companyId}/modules', {
    params: { companyId },
  }) as Promise<Record<string, boolean>>;
}

export interface CompanyModulesUpdate {
  [key: string]: boolean;
}

export function updateCompanyModules(
  companyId: string,
  body: CompanyModulesUpdate,
): Promise<{ company_id?: string } | undefined> {
  return apiPut('/companies/{companyId}/modules', { params: { companyId }, body });
}
